/* Pure verdict rules for the PostHog pulse widget. Low-volume events never drive the verdict: at a handful a day,
   a 50% swing is noise. */
import { EVerdict } from "../homeDashboard.types"

/** Events per day a metric needs in its baseline before its drop counts */
export const MIN_BASELINE_DAILY = 5
export const ACT_DROP_PCT = -50
export const WATCH_DROP_PCT = -25

export interface IPulseMetricLike {
  label: string
  current: number
  baselineDaily: number
  /** Normal events/day floor; keeps a long outage flagged after it has dragged the 7-day baseline down */
  expectedDaily?: number
}

export interface IPulseProductLike {
  metrics: IPulseMetricLike[]
  error: string | null
}

export const deltaPct = (current: number, baselineDaily: number): number | null =>
  baselineDaily > 0 ? Math.round(((current - baselineDaily) / baselineDaily) * 100) : null

export const formatDelta = (delta: number | null) => {
  if (delta === null) return "new"
  return `${delta > 0 ? "+" : ""}${delta}%`
}

/** What "normal" is for a metric: its 7-day average, but never below its expected floor */
export const referenceDaily = (metric: IPulseMetricLike) => Math.max(metric.baselineDaily, metric.expectedDaily ?? 0)

const counts = (metric: IPulseMetricLike) => referenceDaily(metric) >= MIN_BASELINE_DAILY

export const pulseVerdict = (product: IPulseProductLike): { verdict: EVerdict; reason: string } => {
  if (product.error) return { verdict: EVerdict.UNKNOWN, reason: product.error }

  // The first metric is the heartbeat: none at all against a normal baseline means tracking or the app is down
  const [heartbeat] = product.metrics
  if (heartbeat && heartbeat.current === 0 && counts(heartbeat)) {
    return { verdict: EVerdict.ACT, reason: `No ${heartbeat.label} in 24h. Tracking or the app may be down.` }
  }

  const worst = product.metrics
    .filter(counts)
    .map((metric) => ({ metric, delta: deltaPct(metric.current, referenceDaily(metric)) ?? 0 }))
    .sort((a, b) => a.delta - b.delta)[0]
  if (!worst || worst.delta > WATCH_DROP_PCT) return { verdict: EVerdict.CLEAR, reason: "Within normal range" }

  const { metric } = worst
  const against =
    referenceDaily(metric) > metric.baselineDaily ? `expected ${metric.expectedDaily}/day` : "7-day average"
  const reason = `${metric.label} ${formatDelta(worst.delta)} vs ${against}`
  return { verdict: worst.delta <= ACT_DROP_PCT ? EVerdict.ACT : EVerdict.WATCH, reason }
}

const FUNNEL_PCT = 100

/** "240 → 10 (4%) → 0 (0%)": each step's share of the first, so the drop-off reads in one line */
export const funnelSummary = (steps: { count: number }[]): string | null => {
  const first = steps[0]?.count
  if (first === undefined) return null
  return steps
    .map((s, i) =>
      i === 0
        ? s.count.toLocaleString()
        : `${s.count.toLocaleString()} (${first > 0 ? Math.round((s.count / first) * FUNNEL_PCT) : 0}%)`,
    )
    .join(" → ")
}
